import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { CalendarCheck, Check, ChevronLeft, ChevronRight, CircleCheck, Clock3, Download, Minus, Plus, ShieldCheck, ShoppingCart, Star, X } from 'lucide-react';

/* =========================================================
   CONFIGURACIÓN
   ========================================================= */

const CHECKOUT_URL = 'https://aprende-coreano-facil.impultienda.ar/checkout';
const CTA_LABEL = 'Accede ahora';
const META_PIXEL_ID = '2120784785186386';
const MOCKUP_IMAGE = 'https://cdn.magicpatterns.com/uploads/bkRJEMYA1whGnHvRPNi2Wj/mockup-principal.webp';

// Precios base en USD
const PRICE_USD = 9.9;

// Valores de referencia de cada pieza, en pesos argentinos (suman $49.900).
// En el resto de los países se convierten a su moneda.
const MODULE_VALUE_ARS = 3900;
const BONUS_1_VALUE_ARS = 10000;
const BONUS_VALUE_ARS = 5500;

// Argentina: precio fijo, igual al que cobra el checkout de ImpulTienda
const PRICE_ARS = 14990;
// Cambio implícito del checkout (USD 13 = ARS 19.780) para convertir los demás montos en Argentina
const ARS_PER_USD = 19780 / 13;

/* =========================================================
   MONEDA LOCAL POR PAÍS
   AR muestra el precio fijo del checkout.
   El resto muestra "≈" + conversión con el tipo de cambio del día.
   Si algo falla, se queda en USD.
   ========================================================= */

type Currency = {
  code: string;
  prefix: string;
  locale: string;
};
const USD: Currency = {
  code: 'USD',
  prefix: 'USD ',
  locale: 'en-US'
};
const CURRENCY_BY_COUNTRY: Record<string, Currency> = {
  AR: {
    code: 'ARS',
    prefix: '$',
    locale: 'es-AR'
  },
  MX: {
    code: 'MXN',
    prefix: 'MX$',
    locale: 'es-MX'
  },
  PE: {
    code: 'PEN',
    prefix: 'S/ ',
    locale: 'es-PE'
  },
  CL: {
    code: 'CLP',
    prefix: 'CLP$',
    locale: 'es-CL'
  },
  UY: {
    code: 'UYU',
    prefix: '$U ',
    locale: 'es-UY'
  },
  CO: {
    code: 'COP',
    prefix: 'COP$',
    locale: 'es-CO'
  },
  PY: {
    code: 'PYG',
    prefix: '₲ ',
    locale: 'es-PY'
  },
  BO: {
    code: 'BOB',
    prefix: 'Bs ',
    locale: 'es-BO'
  },
  CR: {
    code: 'CRC',
    prefix: '₡',
    locale: 'es-CR'
  },
  GT: {
    code: 'GTQ',
    prefix: 'Q',
    locale: 'es-GT'
  },
  DO: {
    code: 'DOP',
    prefix: 'RD$',
    locale: 'es-DO'
  },
  HN: {
    code: 'HNL',
    prefix: 'L ',
    locale: 'es-HN'
  },
  NI: {
    code: 'NIO',
    prefix: 'C$',
    locale: 'es-NI'
  },
  ES: {
    code: 'EUR',
    prefix: '€',
    locale: 'es-ES'
  },
  // Países dolarizados (o con bolívar inestable) quedan en USD
  EC: USD,
  SV: USD,
  PA: USD,
  PR: USD,
  VE: USD,
  US: USD
};
type PriceState = {
  currency: Currency;
  rate: number;
  approx: boolean;
};
const DEFAULT_PRICE_STATE: PriceState = {
  currency: USD,
  rate: 1,
  approx: false
};
const CACHE_KEY = 'acf-currency-v1';
const CACHE_TTL = 12 * 60 * 60 * 1000;
function readCache(): (PriceState & {
  savedAt: number;
}) | null {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.savedAt > CACHE_TTL) return null;
    return parsed;
  } catch {
    return null;
  }
}
function writeCache(state: PriceState) {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({
      ...state,
      savedAt: Date.now()
    }));
  } catch {
    /* sin almacenamiento: no pasa nada */
  }
}
async function detectCountry(): Promise<string | null> {
  try {
    const r = await fetch('https://get.geojs.io/v1/ip/country.json');
    const j = await r.json();
    if (j?.country) return String(j.country).toUpperCase();
  } catch {
    /* probamos el respaldo */
  }
  try {
    const r = await fetch('https://ipwho.is/?fields=country_code');
    const j = await r.json();
    if (j?.country_code) return String(j.country_code).toUpperCase();
  } catch {
    /* sin país: queda USD */
  }
  return null;
}
async function fetchUsdRate(code: string): Promise<number | null> {
  try {
    const r = await fetch('https://open.er-api.com/v6/latest/USD');
    const j = await r.json();
    const rate = j?.rates?.[code];
    return typeof rate === 'number' && rate > 0 ? rate : null;
  } catch {
    return null;
  }
}
function useLocalPrice(): PriceState {
  const [state, setState] = useState<PriceState>(() => {
    if (typeof window === 'undefined') return DEFAULT_PRICE_STATE;
    const cached = readCache();
    return cached ? {
      currency: cached.currency,
      rate: cached.rate,
      approx: cached.approx
    } : DEFAULT_PRICE_STATE;
  });
  useEffect(() => {
    if (readCache()) return;
    let cancelled = false;
    (async () => {
      const country = await detectCountry();
      const currency = country ? CURRENCY_BY_COUNTRY[country] : undefined;
      if (!currency || currency.code === 'USD') return;
      let next: PriceState | null = null;
      if (currency.code === 'ARS') {
        next = {
          currency,
          rate: ARS_PER_USD,
          approx: false
        };
      } else {
        const rate = await fetchUsdRate(currency.code);
        if (rate) next = {
          currency,
          rate,
          approx: true
        };
      }
      if (next && !cancelled) {
        setState(next);
        writeCache(next);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return state;
}
function roundLocal(value: number, code: string): number {
  if (code === 'USD' || code === 'EUR') return Math.round(value);
  if (code === 'ARS') return Math.round(value / 10) * 10;
  if (value >= 10000) return Math.round(value / 100) * 100;
  if (value >= 1000) return Math.round(value / 10) * 10;
  return Math.round(value);
}

// Precio de venta "terminado en 9" según la magnitud: 9,90 · 179 · 9.790 · 32.990
function charmPrice(value: number): number {
  if (value < 100) return Math.max(0.9, Math.ceil(value) - 0.1);
  if (value < 1000) return Math.round(value / 10) * 10 - 1;
  if (value < 10000) return Math.round(value / 100) * 100 - 10;
  return Math.round(value / 1000) * 1000 - 10;
}
type PriceApi = {
  /** Convierte un monto en USD a la moneda local (número redondeado) */
  amount: (usd: number) => number;
  /** Formatea un monto ya convertido */
  format: (local: number, withApprox?: boolean) => string;
  /** Convierte y formatea en un paso */
  price: (usd: number) => string;
  /** Convierte un valor de referencia en ARS a la moneda local */
  fromArs: (ars: number) => number;
  /** Precio tachado: suma de todos los valores de referencia */
  anchor: number;
  approx: boolean;
  currencyCode: string;
};
const PriceContext = createContext<PriceApi | null>(null);
function PriceProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    currency,
    rate,
    approx
  } = useLocalPrice();
  const api = useMemo<PriceApi>(() => {
    const amount = (usd: number) => {
      if (usd === PRICE_USD) {
        if (currency.code === 'ARS') return PRICE_ARS;
        if (currency.code === 'USD') return PRICE_USD;
        return charmPrice(usd * rate);
      }
      return roundLocal(usd * rate, currency.code);
    };
    const format = (local: number, withApprox = true) => {
      const decimals = Number.isInteger(local) ? 0 : 2;
      const n = local.toLocaleString(currency.locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
      return `${approx && withApprox ? '≈ ' : ''}${currency.prefix}${n}`;
    };
    const fromArs = (ars: number) => currency.code === 'ARS' ? ars : roundLocal(ars / ARS_PER_USD * rate, currency.code);
    const anchor = VALUE_ITEMS.reduce((sum, item) => sum + fromArs(item.ars), 0);
    return {
      amount,
      format,
      price: (usd: number) => format(amount(usd)),
      fromArs,
      anchor,
      approx,
      currencyCode: currency.code
    };
  }, [currency, rate, approx]);
  return <PriceContext.Provider value={api} data-id="element-0" data-mp="2ggdqmt32kipt">{children}</PriceContext.Provider>;
}
function usePrice(): PriceApi {
  const ctx = useContext(PriceContext);
  if (!ctx) throw new Error('usePrice debe usarse dentro de PriceProvider');
  return ctx;
}

/* =========================================================
   CONTENIDO
   ========================================================= */

const HERO_STATS = [{
  value: '6',
  label: 'Módulos paso a paso'
}, {
  value: '4',
  label: 'Bonos incluidos'
}, {
  value: '+100',
  label: 'Mapas visuales'
}, {
  value: '30',
  label: 'Días de plan guiado'
}];
const PAIN_POINTS = ['Quieres aprender coreano pero no sabes por dónde empezar', 'Miras el Hangul y todas las letras te parecen iguales', 'Sabes palabras sueltas de los K-dramas, pero no puedes armar una frase', 'Estudias un día, lo olvidas al siguiente y sientes que no avanzas', 'Saltas entre videos y apps sin un orden claro', 'Pronuncias con miedo porque no sabes si lo estás diciendo bien', 'Piensas que "no eres buena/o para los idiomas"'];
const GAINS = ['Sigues un camino ordenado: primero lo básico, después lo útil y después lo conversacional', 'Reconoces los patrones del Hangul y lees tus primeras palabras', 'Construyes oraciones con sentido desde muy temprano', 'Usas técnicas concretas para retener lo que estudias', 'Tienes todo en un solo lugar, sin saltar de un recurso a otro', 'Pronuncias los sonidos esenciales evitando los errores más comunes', 'Avanzas a tu ritmo con un método pensado para quien empieza de cero'];
const MODULES = [{
  number: '01',
  title: 'Hangul explicado sin complicarte',
  description: 'Entiende cómo funciona el alfabeto coreano de forma clara y paso a paso para que puedas leer y escribir con confianza.',
  tag: 'Paso 1 · La base',
  coverImage: 'https://cdn.magicpatterns.com/uploads/mzM9ck3UC8pgE3L9kJdizD/modulo-01.webp'
}, {
  number: '02',
  title: 'Pronunciación que sí te suena bien',
  description: 'Aprende a producir los sonidos esenciales y evita los errores más comunes al hablar, incluso si nunca estudiaste idiomas.',
  tag: 'Paso 2 · Sonidos clave',
  coverImage: 'https://cdn.magicpatterns.com/uploads/onLoZrjBUdHWtWYCyD9F18/modulo-02.webp'
}, {
  number: '03',
  title: 'Vocabulario esencial',
  description: 'Las palabras que más se usan en el día a día y en los K-dramas, organizadas para que las aprendas sin abrumarte.',
  tag: 'Paso 3 · Palabras útiles',
  coverImage: 'https://cdn.magicpatterns.com/uploads/eR5geqJvn6utMvUA5PnHKT/modulo-03.webp'
}, {
  number: '04',
  title: 'Frases cotidianas y expresiones reales',
  description: 'Expresiones que aparecen en K-dramas y K-pop para que puedas usar el idioma en situaciones reales.',
  tag: 'Paso 4 · Coreano real',
  coverImage: 'https://cdn.magicpatterns.com/uploads/pihQQQrRtdJL3Chu1RPAd1/modulo-04.webp'
}, {
  number: '05',
  title: 'Tus primeras oraciones',
  description: 'La estructura básica para construir frases con sentido y empezar a comunicarte desde muy temprano.',
  tag: 'Paso 5 · Armar frases',
  coverImage: 'https://cdn.magicpatterns.com/uploads/q4xHAje2Fr9mqarbfDJ7GE/modulo-05.webp'
}, {
  number: '06',
  title: 'Memoria y progreso',
  description: 'Tips concretos para memorizar mejor y retener lo que estudias, para que no se te olvide al día siguiente.',
  tag: 'Paso 6 · Retención',
  coverImage: 'https://cdn.magicpatterns.com/uploads/w27k38rk2RJiRxi5ktsEVS/modulo-06.webp'
}];
const BONUSES = [{
  badge: 'BONO 1',
  title: '+100 Mapas Visuales de Coreano Práctico™',
  description: 'Un pack de mapas visuales para repasar Hangul, frases básicas, vocabulario cotidiano, expresiones de K-dramas y coreano para viajar. Para estudiar desde el celular, imprimir y practicar paso a paso.',
  valueArs: BONUS_1_VALUE_ARS,
  image: 'https://cdn.magicpatterns.com/uploads/azQJhQb1NmPneYVHa7geqR/bono-1.webp'
}, {
  badge: 'BONO 2',
  title: 'Guía Hangul Express™: Aprende a leer coreano desde cero',
  description: 'Una guía rápida para entender el alfabeto, reconocer sus letras, comprender cómo se forman las sílabas y leer tus primeras palabras sin sentirte abrumada/o.',
  valueArs: BONUS_VALUE_ARS,
  image: 'https://cdn.magicpatterns.com/uploads/azPobzRhhYADv5nHGDPNZf/bono-2.webp'
}, {
  badge: 'BONO 3',
  title: 'Frases de K-Dramas y K-Pop™',
  description: 'Frases y expresiones frecuentes para reconocer palabras en K-dramas, canciones, entrevistas y contenido coreano. Con traducción, romanización y uso práctico.',
  valueArs: BONUS_VALUE_ARS,
  image: 'https://cdn.magicpatterns.com/uploads/eqkJiU2EwFeLCsRZET1B3A/bono-3.webp'
}, {
  badge: 'BONO 4',
  title: 'Plan 30 Días Coreano Fácil™',
  description: 'Un calendario de estudio simple para saber qué practicar cada día, crear el hábito y avanzar sin perderte ni abandonar a mitad de camino.',
  valueArs: BONUS_VALUE_ARS,
  image: 'https://cdn.magicpatterns.com/uploads/hSHV4tf3mSn4cbGePtrRhN/bono-4.webp'
}];
const VALUE_ITEMS = [{
  label: 'Módulo 1: Hangul explicado',
  ars: MODULE_VALUE_ARS
}, {
  label: 'Módulo 2: Pronunciación',
  ars: MODULE_VALUE_ARS
}, {
  label: 'Módulo 3: Vocabulario esencial',
  ars: MODULE_VALUE_ARS
}, {
  label: 'Módulo 4: Frases cotidianas',
  ars: MODULE_VALUE_ARS
}, {
  label: 'Módulo 5: Tus primeras oraciones',
  ars: MODULE_VALUE_ARS
}, {
  label: 'Módulo 6: Memoria y progreso',
  ars: MODULE_VALUE_ARS
}, {
  label: 'Bono 1: +100 Mapas Visuales',
  ars: BONUS_1_VALUE_ARS
}, {
  label: 'Bono 2: Guía Hangul Express',
  ars: BONUS_VALUE_ARS
}, {
  label: 'Bono 3: Frases de K-Dramas y K-Pop',
  ars: BONUS_VALUE_ARS
}, {
  label: 'Bono 4: Plan 30 Días',
  ars: BONUS_VALUE_ARS
}];
const FOR_YOU = ['Quieres aprender coreano desde cero, sin experiencia previa', 'Miras K-dramas o escuchas K-pop y quieres entender más', 'Nunca estudiaste un idioma y quieres un método simple', 'Prefieres un paso a paso ordenado en lugar de contenido suelto', 'Quieres leer Hangul y armar tus primeras frases', 'Quieres estudiar a tu ritmo, desde el celular o impreso'];
const NOT_FOR_YOU = ['Buscas un curso con clases en vivo o certificación oficial', 'Ya tienes un nivel intermedio o avanzado de coreano', 'Esperas hablar fluido sin practicar', 'No estás dispuesta/o a dedicarle unos minutos por día'];
type Testimonial = {
  text: string;
  name: string;
  initials: string;
  city?: string;
  role?: string;
};

// Testimonios: sin cambios
const TESTIMONIALS: Testimonial[] = [{
  text: "Yo veía K-dramas y me frustraba porque entendía solo palabras sueltas. Con este método logré leer Hangul sin miedo y empecé a pronunciar frases reales en pocos días. Ahora puedo practicar sin sentir que 'no soy buena para los idiomas'.",
  name: 'María González',
  initials: 'MG'
}, {
  text: 'Posta que pensé que el Hangul era imposible y que me iba a costar un huevo. Con las fichas visuales en dos semanas ya estaba leyendo títulos y palabras sueltas de mis doramas favoritos sin subtítulos. El plan de 30 días te salva la vida si no tenés tiempo.',
  name: 'Florencia Martínez',
  initials: 'FM'
}, {
  text: 'Venía viendo videos en YouTube pero no cazaba una, sentía que no tenía método. Este ebook va al hueso: te explica la pronunciación sin vueltas y de forma súper gráfica. Re contra vale la pena por lo que cuesta.',
  name: 'Matías Benítez',
  initials: 'MB'
}, {
  text: 'Llevaba meses intentando aprender coreano por mi cuenta y me atascaba siempre con las vocales y consonantes dobles. La guía visual es una pasada, súper intuitiva y amena. Por fin entiendo cómo se estructuran las frases sin agobiarme.',
  name: 'Laura Sánchez',
  initials: 'LS'
}, {
  text: 'Escucho K-pop a diario y me daba mucha rabia no pillar ni una frase de las canciones. La rutina de 30 días se hace comodísima de seguir después del curro. 100% recomendable para empezar desde cero absoluto.',
  name: 'Javier Gómez',
  initials: 'JG'
}, {
  text: 'De verdad que es seco el método. Antes miraba el Hangul y me daba caleta de miedo porque lo encontraba enredado, pero con los dibujos y fichas se hace súper liviano. Ahora cachar las frases de los K-dramas es otra cosa, me encantó.',
  name: 'Camila Rojas',
  initials: 'CR'
}, {
  text: 'Buenísimo, al tiro le agarré el hilo. No te meten mil reglas lateras de gramática de entrada, sino que vas aprendiendo a hablar y leer al toque. Los bonos de frases de doramas son la guinda de la torta.',
  name: 'Ignacio Valenzuela',
  initials: 'IV'
}, {
  text: "¡Qué purete de curso! Siempre quise aprender coreano pero me daba mba'e miedo no entender nada. Es demasiado claro, visual y fácil de seguir. En menos de un mes ya puedo leer y pronunciar re bien mis primeras oraciones.",
  name: 'Belén Duarte',
  initials: 'BD'
}, {
  text: 'La neta superó mis expectativas. Había descargado mil apps que solo te hacen memorizar palabras sin sentido, pero aquí el método visual te aterriza todo súper rápido. Ya no me siento perdida cuando veo series coreanas.',
  name: 'Sofía Morales',
  initials: 'SM'
}, {
  text: 'Una maravilla total. El contenido es súper chévere y va directo al grano, no te enreda con explicaciones pesadas. El plan diario de 30 días es perfecto para estudiar un ratico cada día sin estrés. ¡Súper recomendado!',
  name: 'Valentina Restrepo',
  initials: 'VR'
}];
const STEPS = [{
  icon: 'cart' as const,
  number: '01',
  title: 'Haz el pago',
  description: 'Completa tu compra de forma segura en menos de un minuto.'
}, {
  icon: 'download' as const,
  number: '02',
  title: 'Descarga al instante',
  description: 'Recibes el acceso inmediato al método y los 4 bonos. Sin esperas, desde cualquier dispositivo.'
}, {
  icon: 'calendar' as const,
  number: '03',
  title: 'Empieza tu plan de 30 días',
  description: 'Sigue el método visual paso a paso y lee tus primeras palabras en Hangul desde el primer día.'
}];
const STEP_ICONS = {
  cart: ShoppingCart,
  download: Download,
  calendar: CalendarCheck
};
const CLOSING_ITEMS = ['6 módulos paso a paso', '4 bonos incluidos', 'Más de 100 mapas visuales', 'Plan guiado de 30 días', 'Acceso inmediato y descarga digital', 'Pago único, sin suscripción', 'Garantía de 7 días'];

// Popups de compra: sin cambios
const PURCHASE_POPUPS = [{
  name: 'Valentina R.',
  location: 'Buenos Aires, Argentina',
  time: 'hace 2 minutos'
}, {
  name: 'Daniela M.',
  location: 'Ciudad de México, México',
  time: 'hace 5 minutos'
}, {
  name: 'Carolina S.',
  location: 'Bogotá, Colombia',
  time: 'hace 8 minutos'
}, {
  name: 'Francisca T.',
  location: 'Santiago, Chile',
  time: 'hace 11 minutos'
}, {
  name: 'Alejandra V.',
  location: 'Lima, Perú',
  time: 'hace 14 minutos'
}, {
  name: 'Mariana F.',
  location: 'Montevideo, Uruguay',
  time: 'hace 18 minutos'
}, {
  name: 'Gabriela P.',
  location: 'Caracas, Venezuela',
  time: 'hace 22 minutos'
}, {
  name: 'Sofía R.',
  location: 'Rosario, Argentina',
  time: 'hace 26 minutos'
}, {
  name: 'Camila V.',
  location: 'Guadalajara, México',
  time: 'hace 31 minutos'
}, {
  name: 'Isabella M.',
  location: 'Medellín, Colombia',
  time: 'hace 35 minutos'
}, {
  name: 'Catalina R.',
  location: 'Valparaíso, Chile',
  time: 'hace 41 minutos'
}, {
  name: 'Luciana B.',
  location: 'Córdoba, Argentina',
  time: 'hace 47 minutos'
}, {
  name: 'Andrea C.',
  location: 'Quito, Ecuador',
  time: 'hace 58 minutos'
}, {
  name: 'Jimena P.',
  location: 'Asunción, Paraguay',
  time: 'hace 1 hora'
}, {
  name: 'Natalia F.',
  location: 'La Paz, Bolivia',
  time: 'hace 1 hora y 8 min'
}, {
  name: 'Paola M.',
  location: 'San José, Costa Rica',
  time: 'hace 1 hora y 15 min'
}, {
  name: 'Valeria T.',
  location: 'Guatemala, Guatemala',
  time: 'hace 1 hora y 24 min'
}, {
  name: 'Mónica R.',
  location: 'Mendoza, Argentina',
  time: 'hace 1 hora y 33 min'
}];
const POPUP_TIMING = {
  desktop: {
    initialDelay: 8000,
    visible: 4000,
    interval: 8000
  },
  mobile: {
    initialDelay: 10000,
    visible: 4000,
    interval: 8000
  }
};
const FAQS = [{
  question: '¿Es para principiantes reales?',
  answer: 'Sí. Está pensado para quien empieza de cero, incluso si nunca estudió otro idioma.'
}, {
  question: '¿Puedo aprender aunque no tenga buena base en idiomas?',
  answer: 'Sí. El método es visual y va paso a paso: primero lo básico, después lo útil y después lo conversacional.'
}, {
  question: '¿Cómo recibo el material después de comprar?',
  answer: 'Recibes el acceso apenas se confirma el pago. Sin esperas y desde cualquier dispositivo.'
}, {
  question: '¿El pago es único o es una suscripción?',
  answer: 'Es un pago único. Sin cuotas mensuales ni cargos extra. El acceso es de por vida.'
}, {
  question: '¿Incluye pronunciación y cómo evitar errores?',
  answer: 'Sí. El módulo de pronunciación te enseña los sonidos esenciales y los errores más comunes al hablar.'
}, {
  question: '¿Qué pasa si no me sirve?',
  answer: 'Tienes 7 días de garantía. Si no es para ti, te devolvemos el 100% de tu dinero.'
}];
const fadeUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    amount: 0.2
  }
};

/* =========================================================
   META PIXEL (inyectado por código: el snippet normal rompe Magic)
   ========================================================= */

function MetaPixel() {
  useEffect(() => {
    const w = window as any;
    if (w.__metaPixelInitialized === META_PIXEL_ID) return;
    w.__metaPixelInitialized = META_PIXEL_ID;
    if (!w.fbq) {
      const fbq: any = (...args: any[]) => {
        fbq.callMethod ? fbq.callMethod(...args) : fbq.queue.push(args);
      };
      fbq.queue = [];
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = '2.0';
      w.fbq = fbq;
      if (!w._fbq) w._fbq = fbq;
      const s = document.createElement('script');
      s.async = true;
      s.src = 'https://connect.facebook.net/en_US/fbevents.js';
      s.id = 'meta-pixel-script';
      const first = document.getElementsByTagName('script')[0];
      if (first?.parentNode) first.parentNode.insertBefore(s, first);else document.head.appendChild(s);
    }
    w.fbq('init', META_PIXEL_ID);
    w.fbq('track', 'PageView');
  }, []);
  return null;
}

/* =========================================================
   COMPONENTES
   ========================================================= */

// Animaciones globales: brillo de los CTA y emojis flotantes
function GlobalStyles() {
  return <style data-id="element-1" data-mp="3p7ymg4oi1mm0">{`
      @keyframes acf-shine {
        0% { transform: translateX(-130%) skewX(-20deg); }
        60%, 100% { transform: translateX(230%) skewX(-20deg); }
      }
      @keyframes acf-float-up {
        0% { transform: translate3d(0, 0, 0) rotate(0deg); opacity: 0; }
        10% { opacity: var(--acf-op); }
        50% { transform: translate3d(var(--acf-sway), -55vh, 0) rotate(12deg); }
        90% { opacity: var(--acf-op); }
        100% { transform: translate3d(0, -115vh, 0) rotate(-8deg); opacity: 0; }
      }
      .acf-shine { animation: acf-shine 3.2s ease-in-out infinite; }
      .acf-emoji { animation-name: acf-float-up; animation-timing-function: linear; animation-iteration-count: infinite; }
      @media (prefers-reduced-motion: reduce) {
        .acf-shine, .acf-emoji { animation: none !important; display: none; }
      }
    `}</style>;
}

// Baja a una sección de la página sin recargarla (en Magic los enlaces "#..." recargan y vuelven arriba)
function scrollToSection(e: React.MouseEvent<HTMLAnchorElement>, href: string) {
  if (!href.startsWith('#')) return;
  const target = document.getElementById(href.slice(1));
  if (!target) return;
  e.preventDefault();
  (e.currentTarget as HTMLElement).blur();
  // Se ejecuta después del toque para que nada vuelva a subir la página
  window.setTimeout(() => {
    const topBar = 44;
    const start = window.scrollY;
    const end = target.getBoundingClientRect().top + start - topBar;
    const duration = 700;
    const t0 = performance.now();
    const ease = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const step = () => {
      const p = Math.min(1, (performance.now() - t0) / duration);
      window.scrollTo(0, start + (end - start) * ease(p));
      if (p < 1) window.setTimeout(step, 16);
    };
    step();
  }, 50);
}
function CtaButton({
  href = '#comprar',
  className = ''
}: {
  href?: string;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();
  return <motion.a href={href} onClick={e => scrollToSection(e, href)} animate={reduceMotion ? undefined : {
    scale: [1, 1.035, 1]
  }} transition={{
    duration: 1.8,
    ease: 'easeInOut',
    repeat: Infinity,
    repeatDelay: 0.6
  }} whileHover={{
    scale: 1.04
  }} whileTap={{
    scale: 0.97
  }} className={`cta-button relative flex min-h-[54px] w-full items-center justify-center overflow-hidden rounded-cta bg-pink px-6 py-4 text-center text-base font-extrabold text-surface shadow-cta focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink sm:text-[17px] ${className}`} data-id="element-2" data-mp="yucl9tsre1ig">
      <span aria-hidden="true" className="acf-shine pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/35 to-transparent" data-id="element-3" data-mp="28rjenhbd56i5" />
      <span className="relative" data-id="element-4" data-mp="2inw7kcz33gbu">{CTA_LABEL}</span>
    </motion.a>;
}

/* Timer de reserva de 15 minutos (se guarda por visitante) */
const RESERVATION_MINUTES = 15;
const RESERVATION_KEY = 'acf-reserva-v1';
function getReservationDeadline(): number {
  const now = Date.now();
  try {
    const saved = Number(window.localStorage.getItem(RESERVATION_KEY));
    // Si la reserva venció hace más de 1 hora, se abre una nueva
    if (saved && now < saved + 60 * 60 * 1000) return saved;
  } catch {
    /* sin almacenamiento: reserva solo para esta visita */
  }
  const deadline = now + RESERVATION_MINUTES * 60 * 1000;
  try {
    window.localStorage.setItem(RESERVATION_KEY, String(deadline));
  } catch {
    /* sin almacenamiento */
  }
  return deadline;
}
function ReservationTimer() {
  const [deadline] = useState(() => typeof window !== 'undefined' ? getReservationDeadline() : Date.now() + RESERVATION_MINUTES * 60000);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const remaining = Math.max(0, deadline - now);
  const expired = remaining === 0;
  const mm = String(Math.floor(remaining / 60000)).padStart(2, '0');
  const ss = String(Math.floor(remaining % 60000 / 1000)).padStart(2, '0');
  const progress = remaining / (RESERVATION_MINUTES * 60000) * 100;
  return <div className="mt-3 rounded-xl border border-pink/40 bg-blush px-4 py-3 text-center" role="timer" aria-live="off" data-id="element-5" data-mp="2xec443mtugqw">
      {!expired ? <>
          <p className="flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-[0.12em] text-pink" data-id="element-6" data-mp="3lxnmiopuhawo">
            <Clock3 className="h-3.5 w-3.5" aria-hidden="true" data-id="element-7" data-mp="36zbqmkaaq66k" />
            Tu descuento está reservado
          </p>
          <p className="mt-1 font-display text-3xl font-bold leading-none text-foreground" data-id="element-8" data-mp="3bxdav8jgkbjm">
            {mm}:{ss}
          </p>
          <div className="mx-auto mt-2 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-surface" data-id="element-9" data-mp="32yjhdr1fc1aa">
            <div className="h-full rounded-full bg-pink transition-[width] duration-1000 ease-linear" style={{
          width: `${progress}%`
        }} data-id="element-10" data-mp="2o9vojsiyq5o1" />
          </div>
          <p className="mt-2 text-xs leading-relaxed text-muted" data-id="element-11" data-mp="2t988o5ig8ymx">
            Reservamos este precio especial para ti durante {RESERVATION_MINUTES} minutos. Al finalizar el tiempo, el
            cupo con descuento se libera para la siguiente persona en espera.
          </p>
        </> : <>
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-pink" data-id="element-12" data-mp="2zpa1v65odofc">Tu tiempo de reserva finalizó</p>
          <p className="mt-1 text-xs leading-relaxed text-muted" data-id="element-13" data-mp="2up47hi80tntk">
            El descuento puede liberarse para otra persona en cualquier momento. Si todavía lo ves disponible,
            asegúralo ahora.
          </p>
        </>}
    </div>;
}

/* Emojis flotantes de fondo: suaves, transparentes y sin bloquear clics */
const FLOATING_EMOJIS = ['🌸', '💜', '✨', '한', '📚', '💖', '🎧', '글', '⭐', '🌸'];
function FloatingEmojis() {
  const reduceMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const items = useMemo(() => {
    const count = isMobile ? 6 : 10;
    return Array.from({
      length: count
    }).map((_, i) => ({
      char: FLOATING_EMOJIS[i % FLOATING_EMOJIS.length],
      left: 4 + i * 97 % 92,
      size: 16 + i * 7 % 12,
      duration: 22 + i * 5 % 14,
      delay: -(i * 3.7 % 30),
      sway: `${i % 2 === 0 ? '' : '-'}${20 + i * 11 % 30}px`,
      opacity: 0.22 + i * 3 % 4 * 0.04
    }));
  }, [isMobile]);
  if (reduceMotion) return null;
  return <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-30 overflow-hidden" data-id="element-14" data-mp="hkierpgxymix">
      {items.map((e, i) => <span key={i} className="acf-emoji absolute select-none font-bold text-pink" style={{
      left: `${e.left}%`,
      bottom: '-40px',
      fontSize: `${e.size}px`,
      animationDuration: `${e.duration}s`,
      animationDelay: `${e.delay}s`,
      '--acf-op': e.opacity,
      '--acf-sway': e.sway
    } as React.CSSProperties} data-id="element-15" data-mp="miagtcbavp0u" data-mpi={i}>
          {e.char}
        </span>)}
    </div>;
}
function TopBar() {
  return <div className="sticky top-0 z-40 flex min-h-[36px] w-full items-center justify-center bg-primary px-4 py-2 text-center" data-id="element-16" data-mp="379kpfcqhupqc">
      <p className="text-[11px] font-medium leading-snug text-surface sm:text-xs" data-id="element-17" data-mp="2gz5aetue5lmr">
        Acceso inmediato · Pago único · Pagas en tu moneda · Garantía de 7 días
      </p>
    </div>;
}
function Hero() {
  return <section className="w-full bg-ink px-5 pb-12 pt-10 sm:pb-20 sm:pt-16" data-id="element-18" data-mp="27xxiqumdrj1n">
      <div className="mx-auto flex max-w-3xl flex-col items-center text-center" data-id="element-19" data-mp="2c012s7n47vyj">
        <span className="mb-6 inline-block rounded-full border border-primary bg-primary/20 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-surface" data-id="element-20" data-mp="1y95pmj5l2sp5">
          Método visual de coreano desde cero · 2026
        </span>
        <h1 className="font-display text-[26px] font-bold leading-tight text-surface sm:text-5xl md:text-[56px]" data-id="element-21" data-mp="1oc16ks71bdck">
          Pasa de no entender ni una letra a <span className="text-pink-light" data-id="element-22" data-mp="1t7qa16m6dee5">leer Hangul</span> y arma tus primeras
          frases en coreano en <span className="text-pink-light" data-id="element-23" data-mp="1k9ei919koak4">menos de 7 días</span>.
        </h1>
        <figure className="relative mt-8 w-full max-w-[520px] sm:mt-10" data-id="element-24" data-mp="1kbh5xen95rlo">
          <div className="relative overflow-hidden rounded-media shadow-media" data-id="element-25" data-mp="10oe2o6yfmqn2">
            <img src={MOCKUP_IMAGE} alt="Ebook Aprende Coreano Fácil junto a sus 4 bonos" width="1200" height="1200" fetchPriority="high" loading="eager" decoding="async" sizes="(max-width: 560px) calc(100vw - 40px), 520px" className="block aspect-square w-full object-cover" data-id="element-26" data-mp="vpjj32sn4y1w" />
          </div>
          <div className="absolute -bottom-4 -left-2 flex h-20 w-20 flex-col items-center justify-center rounded-full border-2 border-surface bg-pink text-surface shadow-cta sm:-left-5 sm:h-24 sm:w-24" data-id="element-27" data-mp="1en3xffxs4e89">
            <ShieldCheck className="h-6 w-6 sm:h-7 sm:w-7" aria-hidden="true" data-id="element-28" data-mp="1odytfc0tus6w" />
            <span className="mt-0.5 text-[10px] font-extrabold uppercase leading-tight sm:text-[11px]" data-id="element-29" data-mp="22dq1yx8krgdi">
              Garantía
              <br data-id="element-30" data-mp="1xbsgrxgm6bhx" />7 días
            </span>
          </div>
          <figcaption className="px-3 pt-6 text-center text-xs font-medium text-surface/75 sm:text-sm" data-id="element-31" data-mp="v1id0kx2pp2h">
            El método completo, listo para empezar hoy mismo.
          </figcaption>
        </figure>
        <p className="mt-6 max-w-xl text-sm leading-relaxed text-surface/80 sm:text-base" data-id="element-32" data-mp="140c641b73xba">
          6 módulos paso a paso + 4 bonos + más de 100 mapas visuales. Hangul, pronunciación, vocabulario y frases
          reales de K-dramas y K-pop, con un método simple, visual y ordenado. Aunque nunca hayas estudiado idiomas.
        </p>
        <div className="mt-8 w-full max-w-md" data-id="element-33" data-mp="1is4ic4fp0v8s">
          <CtaButton data-id="element-34" data-mp="1nouxmpjobmt5" />
        </div>
        <div className="mt-8 grid w-full grid-cols-2 overflow-hidden rounded-2xl border border-surface/10 bg-ink-card sm:mt-10 sm:grid-cols-4" data-id="element-35" data-mp="1npwamzk92jdi">
          {HERO_STATS.map((s, i) => <div key={s.label} className={`flex flex-col items-center border-surface/10 px-4 py-5 sm:py-6 ${i % 2 !== 0 ? 'border-l' : ''} ${i >= 2 ? 'border-t' : ''} sm:border-t-0 sm:border-l sm:first:border-l-0`} data-id="element-36" data-mp="3pi2ei3exqch5" data-mpi={i}>
              <span className="font-display text-4xl font-bold text-primary-soft" data-id="element-37" data-mp="3h721y9scntl5">{s.value}</span>
              <span className="mt-1 text-xs font-medium text-surface/85 sm:text-sm" data-id="element-38" data-mp="3c9k2da082qpt">{s.label}</span>
            </div>)}
        </div>
      </div>
    </section>;
}
function SocialProofBar() {
  return <section className="w-full bg-blush px-5 py-10" data-id="element-39" data-mp="8riczmypoxbw">
      <div className="mx-auto flex max-w-3xl flex-col items-center text-center" data-id="element-40" data-mp="3sfhou8ja5h4">
        <p className="text-sm font-medium text-foreground sm:text-base" data-id="element-41" data-mp="3vwq3nc4g0u9e">
          <span className="tracking-wider text-primary" aria-label="5 de 5 estrellas" data-id="element-42" data-mp="3vuwxrz1nme2g">
            ★★★★★
          </span>{' '}
          4.9/5 · Más de 500 personas ya empezaron a aprender coreano con este método
        </p>
      </div>
    </section>;
}
function Identification() {
  return <section className="w-full bg-background px-5 py-12 sm:py-20" data-id="element-43" data-mp="2fh6qkjf8n9ol">
      <div className="mx-auto max-w-5xl" data-id="element-44" data-mp="78qrxj6nn7wn">
        <div className="mx-auto max-w-2xl text-center" data-id="element-45" data-mp="77p56hga0wz3">
          <h2 className="font-display text-2xl font-bold leading-tight text-foreground sm:text-4xl" data-id="element-46" data-mp="c1jv42mh686m">
            ¿Te identificas con alguna de <span className="text-pink" data-id="element-47" data-mp="3ugc9r63y3wwf">estas situaciones</span>?
          </h2>
          <p className="mt-3 text-sm italic text-muted sm:text-base" data-id="element-48" data-mp="2bi535akafp2">
            Si respondes sí a una sola, este método fue creado para ti.
          </p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2" data-id="element-49" data-mp="uzw49oddzi27">
          <motion.div {...fadeUp} transition={{
          duration: 0.5
        }} className="rounded-2xl border border-line border-l-4 border-l-red-300 bg-surface p-6 sm:p-8" data-id="element-50" data-mp="ux9xhqybl6l1">
            <h3 className="mb-5 text-lg font-bold text-foreground sm:text-xl" data-id="element-51" data-mp="m0iwr18tp3k4">Sin el método</h3>
            <ul className="space-y-3" data-id="element-52" data-mp="31s6rqr4a0m0a">
              {PAIN_POINTS.map(item => <li key={item} className="flex items-start gap-3" data-id="element-53" data-mp="36q86x0yl016h">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-500" data-id="element-54" data-mp="2y6v41qqc0r9v">
                    <X className="h-3.5 w-3.5" aria-hidden="true" data-id="element-55" data-mp="2u8dyvcqh1qv3" />
                  </span>
                  <span className="text-sm leading-relaxed text-muted" data-id="element-56" data-mp="385axw1gquen2">{item}</span>
                </li>)}
            </ul>
          </motion.div>
          <motion.div {...fadeUp} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="rounded-2xl border-l-4 border-primary bg-lilac p-6 sm:p-8" data-id="element-57" data-mp="2fgn9vy4m1b5t">
            <h3 className="mb-5 text-lg font-bold text-primary-strong sm:text-xl" data-id="element-58" data-mp="2p7hvr1dy28qq">Con Aprende Coreano Fácil™</h3>
            <ul className="space-y-3" data-id="element-59" data-mp="2kfgm7n85uw4e">
              {GAINS.map(item => <li key={item} className="flex items-start gap-3" data-id="element-60" data-mp="1wqdutnnq5owp">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary" data-id="element-61" data-mp="1wokne65af0nn">
                    <Check className="h-3.5 w-3.5" aria-hidden="true" data-id="element-62" data-mp="3rulm4rh5bu4p" />
                  </span>
                  <span className="text-sm leading-relaxed text-foreground/85" data-id="element-63" data-mp="n93s94j1an7">{item}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>;
}
function ProductIntro() {
  return <section className="w-full bg-blush px-5 py-12 sm:py-20" data-id="element-64" data-mp="9fb2o2yy6ivd">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      amount: 0.3
    }} transition={{
      duration: 0.5
    }} className="mx-auto flex max-w-2xl flex-col items-center text-center" data-id="element-65" data-mp="38vzjh85zduyo">
        <span className="mb-5 inline-block rounded-full border border-primary/40 bg-surface px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary-strong" data-id="element-66" data-mp="3muqz4z0hroox">
          Lo que incluye el método
        </span>
        <h2 className="font-display text-3xl font-bold leading-tight text-foreground sm:text-5xl" data-id="element-67" data-mp="123qgim6cev54">
          Aprende Coreano Fácil™: <span className="text-pink" data-id="element-68" data-mp="1c86xxjzh0kb1">Método Visual</span> desde Cero
        </h2>
        <p className="mt-5 text-base font-medium text-primary-strong sm:text-lg" data-id="element-69" data-mp="17an95q55cp4u">
          El método visual para empezar a entender coreano aunque nunca hayas estudiado un idioma.
        </p>
        <div className="mx-auto mt-6 h-px w-16 bg-primary/40" data-id="element-70" data-mp="yg8tvnkm9elq" />
        <p className="mt-6 text-sm leading-relaxed text-muted sm:text-base" data-id="element-71" data-mp="ti7b73r3rhas">
          6 módulos organizados paso a paso, más 4 bonos y más de 100 mapas visuales para repasar. Diseñado para que
          cada día de estudio te acerque a leer, pronunciar y usar coreano real.
        </p>
      </motion.div>
    </section>;
}
function Modules() {
  return <section className="w-full bg-background px-5 py-12 sm:py-20" data-id="element-72" data-mp="fk0l49seallo">
      <div className="mx-auto max-w-5xl" data-id="element-73" data-mp="2dyc193h6tlfx">
        <div className="mx-auto max-w-2xl text-center" data-id="element-74" data-mp="2dx96ulknp0v9">
          <h2 className="font-display text-2xl font-bold leading-tight text-foreground sm:text-4xl" data-id="element-75" data-mp="29w78e6ykcsuc">
            6 Módulos <span className="text-pink" data-id="element-76" data-mp="2t6i5tyxjpku7">paso a paso</span>
          </h2>
          <p className="mt-3 text-sm text-muted sm:text-base" data-id="element-77" data-mp="2x7bu4tewh1nm">Todo lo que necesitas, en el orden correcto.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-3 min-[480px]:gap-4 sm:gap-6 md:grid-cols-2" data-id="element-78" data-mp="2jeci51ebtpkt">
          {MODULES.map((m, i) => <motion.article key={m.number} {...fadeUp} transition={{
          duration: 0.45,
          delay: i % 2 * 0.08
        }} className="flex gap-3 rounded-2xl border border-line bg-surface p-4 shadow-card min-[480px]:gap-5 min-[480px]:p-6 sm:gap-6 sm:p-7" data-id="element-79" data-mp="2ocdy3dggh8s2" data-mpi={i}>
              <div className="flex min-w-0 flex-1 flex-col" data-id="element-80" data-mp="20iw46w7voq9z">
                <span className="font-display text-3xl font-bold leading-none text-primary sm:text-4xl" data-id="element-81" data-mp="25gfk00k85b41">{m.number}</span>
                <h3 className="mt-3 text-base font-bold leading-snug text-foreground sm:text-lg" data-id="element-82" data-mp="3ushm3uejhzg5">{m.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted" data-id="element-83" data-mp="3lx0wrb61lr1">{m.description}</p>
                <span className="mt-5 inline-block w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary-strong" data-id="element-84" data-mp="dptvpv3p7ycl">
                  {m.tag}
                </span>
              </div>
              <img src={m.coverImage} alt={`Módulo ${m.number}: ${m.title} – Aprende Coreano Fácil`} width="480" height="640" loading="lazy" decoding="async" className="block aspect-[3/4] w-[32%] shrink-0 self-start rounded-xl object-cover shadow-mockup min-[480px]:w-[140px]" data-id="element-85" data-mp="mmcon7bxjm96" />
            </motion.article>)}
        </div>
      </div>
    </section>;
}
function Bonuses() {
  const {
    fromArs,
    format
  } = usePrice();
  return <section className="w-full bg-ink px-5 py-12 sm:py-20" data-id="element-86" data-mp="37b91ewomd6q2">
      <div className="mx-auto max-w-5xl" data-id="element-87" data-mp="339ngqp5ufo5b">
        <div className="mx-auto max-w-2xl text-center" data-id="element-88" data-mp="33aq80y72vslr">
          <h2 className="font-display text-2xl font-bold leading-tight text-surface sm:text-4xl" data-id="element-89" data-mp="1ihognrw19qc2">
            🎁 4 Bonos <span className="text-pink-light" data-id="element-90" data-mp="1djwr34iv2gb1">incluidos</span>
          </h2>
          <p className="mt-3 text-sm text-surface/75 sm:text-base" data-id="element-91" data-mp="1rimn0gty29xt">Sin costo adicional. Incluidos en tu acceso de hoy.</p>
        </div>
        <div className="mt-10 grid gap-4 sm:gap-6 md:grid-cols-2" data-id="element-92" data-mp="1ng86i3dqzm9p">
          {BONUSES.map((b, i) => <motion.article key={b.badge} {...fadeUp} transition={{
          duration: 0.45,
          delay: i % 2 * 0.08
        }} className="flex flex-col rounded-2xl border border-surface/10 bg-ink-card p-5 sm:p-6" data-id="element-93" data-mp="21fr9khrpc7f5" data-mpi={i}>
              <img src={b.image} alt={`Mockup del ${b.badge.toLowerCase()}: ${b.title}`} width="800" height="800" loading="lazy" decoding="async" className="block aspect-square w-full rounded-media object-cover" data-id="element-94" data-mp="26790ed00grme" />
              <span className="mt-5 w-fit rounded-md bg-blush px-2.5 py-1 text-[11px] font-bold tracking-wide text-pink" data-id="element-95" data-mp="2yus9h0t1rbt1">
                {b.badge}
              </span>
              <h3 className="mt-3 text-base font-bold leading-snug text-surface sm:text-lg" data-id="element-96" data-mp="2puc7jvryccqa">{b.title}</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-surface/75" data-id="element-97" data-mp="2tu4j8hcn6yzv">{b.description}</p>
              <div className="mt-5 flex items-center justify-between gap-3" data-id="element-98" data-mp="2g2q08aiqixst">
                <span className="text-sm text-line line-through" data-id="element-99" data-mp="2kuzlhd8wn9or">Valor {format(fromArs(b.valueArs))}</span>
                <span className="shrink-0 rounded-full bg-pink px-3 py-1 text-xs font-bold text-surface" data-id="element-100" data-mp="2663d1f2xrunm">
                  INCLUIDO HOY
                </span>
              </div>
            </motion.article>)}
        </div>
      </div>
    </section>;
}
function Offer() {
  const {
    amount,
    format,
    fromArs,
    anchor,
    approx
  } = usePrice();
  const priceLocal = amount(PRICE_USD);
  const anchorLocal = anchor;
  const savings = format(roundLocal(anchorLocal - priceLocal, 'USD'));
  const discount = Math.round((1 - priceLocal / anchorLocal) * 100);
  return <section id="comprar" className="w-full scroll-mt-20 bg-surface px-5 py-12 sm:py-20" data-id="element-101" data-mp="3u3canm0xduk3">
      <motion.div {...fadeUp} transition={{
      duration: 0.5
    }} className="mx-auto max-w-[600px]" data-id="element-102" data-mp="3u0zuneeq9u9o">
        <h2 className="text-center font-display text-2xl font-bold text-foreground sm:text-4xl" data-id="element-103" data-mp="3pzxtut55xa5l">
          Todo lo que <span className="text-pink" data-id="element-104" data-mp="lnj6m6ns6bjx">recibes hoy</span>
        </h2>
        <div className="mt-6 rounded-2xl border border-line p-4 sm:mt-8 sm:p-6" data-id="element-105" data-mp="loc4ei4rhqpx">
          <ul data-id="element-106" data-mp="hkmhry7ndj7j">
            {VALUE_ITEMS.map(item => <li key={item.label} className="flex items-center justify-between gap-3 border-b border-dashed border-line py-2 last:border-b-0 sm:py-2.5" data-id="element-107" data-mp="cn4hsxblyebo">
                <span className="text-[13px] text-foreground/85 sm:text-sm" data-id="element-108" data-mp="2xdd2hsoosy21">{item.label}</span>
                <span className="shrink-0 text-[13px] font-medium text-muted sm:text-sm" data-id="element-109" data-mp="1cvaxrweyqldn">{format(fromArs(item.ars))}</span>
              </li>)}
          </ul>
          <div className="my-4 h-px w-full bg-line" data-id="element-110" data-mp="1hs1e8kuojizl" />
          <div className="flex items-center justify-between gap-3" data-id="element-111" data-mp="1lvh5f1nzcr4w">
            <span className="text-[13px] text-muted sm:text-sm" data-id="element-112" data-mp="1qmfqsfntsff0">Valor total del método</span>
            <span className="text-lg font-medium text-line line-through decoration-muted/60 sm:text-xl" data-id="element-113" data-mp="1vlixe4jgetp3">
              {format(anchorLocal)}
            </span>
          </div>
          <div className="mt-4 flex flex-col items-center rounded-xl bg-blush py-5 text-center sm:py-6" data-id="element-114" data-mp="20isl9fr72xat">
            <span className="text-sm font-medium text-foreground" data-id="element-115" data-mp="25hlw10f3ka7a">Tu inversión hoy</span>
            <span className="font-display text-5xl font-bold text-pink sm:text-6xl" data-id="element-116" data-mp="29kik36neoyim">{format(priceLocal)}</span>
            <span className="mt-3 inline-flex items-center rounded-full border border-pink/30 bg-surface px-3 py-1 text-xs font-bold text-pink sm:text-sm" data-id="element-117" data-mp="b7s3yi3p69tm">
              Ahorras {savings} ({discount}% OFF)
            </span>
            <p className="mt-3 px-4 text-xs text-muted sm:text-sm" data-id="element-118" data-mp="3c16kwd8g0yhz">
              Pago único · Sin suscripción · Acceso de por vida
            </p>
            {approx && <p className="mt-1 px-4 text-[11px] text-muted" data-id="element-119" data-mp="3pylc9ni9bgcl">Precio aproximado en tu moneda. El monto exacto lo ves al pagar.</p>}
          </div>
        </div>
        <div className="mt-6 sm:mt-8" data-id="element-120" data-mp="2xcjvfip3qqd1">
          <CtaButton href={CHECKOUT_URL} data-id="element-121" data-mp="2scxilad6rc4r" />
          <ReservationTimer data-id="element-122" data-mp="2sdysvcmdnulf" />
          <div className="mt-4 rounded-xl border border-primary/20 bg-lilac px-4 py-3 text-center" data-id="element-123" data-mp="2seqq0rvmi0dt">
            <p className="text-sm font-bold text-primary-strong" data-id="element-124" data-mp="3734u164rzuvy">Paga fácil desde tu país</p>
            <p className="mt-1 text-xs leading-relaxed text-muted sm:text-sm" data-id="element-125" data-mp="329sermlzl0fv">
              Pagas en tu moneda y con tu medio de pago habitual. Recibes el acceso al instante en tu correo.
            </p>
          </div>
        </div>
      </motion.div>
    </section>;
}
function ForWho() {
  return <section className="w-full bg-blush px-5 py-12 sm:py-20" data-id="element-126" data-mp="p32kvjoe7e8n">
      <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2" data-id="element-127" data-mp="tw5gdxcqcxrh">
        <motion.div {...fadeUp} transition={{
        duration: 0.5
      }} className="rounded-2xl border border-line bg-surface p-6 sm:p-8" data-id="element-128" data-mp="g3z08b9kuqs1">
          <h3 className="mb-6 text-lg font-bold text-primary-strong sm:text-xl" data-id="element-129" data-mp="k6c4swcl8in8">Este método es para ti si…</h3>
          <ul className="space-y-4" data-id="element-130" data-mp="17w6h9cszw56y">
            {FOR_YOU.map(item => <li key={item} className="flex items-start gap-3" data-id="element-131" data-mp="1cu7zjvilk0hd">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary" data-id="element-132" data-mp="1crlp9o4bn6vg">
                  <Check className="h-3.5 w-3.5" aria-hidden="true" data-id="element-133" data-mp="13si9at0gb7l6" />
                </span>
                <span className="text-sm leading-relaxed text-foreground/85" data-id="element-134" data-mp="3jjwb6mfkc2rf">{item}</span>
              </li>)}
          </ul>
        </motion.div>
        <motion.div {...fadeUp} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="rounded-2xl border border-line bg-surface p-6 sm:p-8" data-id="element-135" data-mp="3ohfonk4fsbib">
          <h3 className="mb-6 text-lg font-bold text-foreground sm:text-xl" data-id="element-136" data-mp="1p4qv2fdg0kdk">No es para ti si…</h3>
          <ul className="space-y-4" data-id="element-137" data-mp="1kbxqpslvze20">
            {NOT_FOR_YOU.map(item => <li key={item} className="flex items-start gap-3" data-id="element-138" data-mp="1gauedg31s05j">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-500" data-id="element-139" data-mp="1bcj3d210njlq">
                  <X className="h-3.5 w-3.5" aria-hidden="true" data-id="element-140" data-mp="16cobav5i8nz3" />
                </span>
                <span className="text-sm leading-relaxed text-muted" data-id="element-141" data-mp="11duxf1mb2uye">{item}</span>
              </li>)}
          </ul>
        </motion.div>
      </div>
    </section>;
}
function Stars() {
  return <div className="flex gap-0.5" aria-label="5 de 5 estrellas" data-id="element-142" data-mp="2vrme2l31yf3c">
      {Array.from({
      length: 5
    }).map((_, i) => <Star key={i} className="h-[18px] w-[18px] fill-primary text-primary" aria-hidden="true" data-id="element-143" data-mp="2rrkc0atenjnj" data-mpi={i} />)}
    </div>;
}
function TestimonialCard({
  t
}: {
  t: Testimonial;
}) {
  return <article className="flex h-full flex-col rounded-2xl border border-line bg-surface p-6 shadow-card" data-id="element-144" data-mp="ih35jreglsy5">
      <Stars data-id="element-145" data-mp="nae5r9ornbh3" />
      <p className="mt-4 flex-1 text-sm leading-relaxed text-foreground/85" data-id="element-146" data-mp="n9w8c9qdar7l">“{t.text}”</p>
      <div className="mt-6 flex items-center gap-3" data-id="element-147" data-mp="ra6fvqtt5pok">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-surface" data-id="element-148" data-mp="3qqcsnd7xtj9e">
          {t.initials}
        </span>
        <div data-id="element-149" data-mp="3vqr28zp6t19j">
          <p className="text-sm font-bold text-foreground" data-id="element-150" data-mp="3l3qtxwuguk1">{t.name}</p>
          {t.city && <p className="text-xs text-muted" data-id="element-151" data-mp="3ngfsv10s56l">{t.city}</p>}
          {t.role && <p className="text-xs italic text-primary-strong" data-id="element-152" data-mp="8lroh2fkw9n8">{t.role}</p>}
        </div>
      </div>
    </article>;
}
function Testimonials() {
  const reducedMotion = useMemo(() => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches, []);
  const plugins = useMemo(() => reducedMotion ? [] : [Autoplay({
    delay: 4500,
    stopOnMouseEnter: true,
    stopOnInteraction: false
  })], [reducedMotion]);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    duration: 28
  }, plugins);
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState<number[]>([]);
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    const onInit = () => {
      setSnaps(emblaApi.scrollSnapList());
      onSelect();
    };
    onInit();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onInit);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onInit);
    };
  }, [emblaApi]);
  const prev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const goTo = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi]);
  return <section className="w-full bg-background px-5 py-12 sm:py-20" data-id="element-153" data-mp="3b0ltotpejq5k">
      <div className="mx-auto max-w-5xl" data-id="element-154" data-mp="3f0ciwsza5y7z">
        <div className="mx-auto max-w-2xl text-center" data-id="element-155" data-mp="3f38ggg77ukvw">
          <h2 className="font-display text-2xl font-bold leading-tight text-foreground sm:text-4xl" data-id="element-156" data-mp="318xvm9ycbav4">
            Lo que dicen quienes ya <span className="text-pink" data-id="element-157" data-mp="3679aiz3hsbk7">empezaron</span>
          </h2>
          <p className="mt-3 text-sm text-muted sm:text-base" data-id="element-158" data-mp="2rarvnxw3gesw">
            <span className="tracking-wider text-primary" aria-label="5 de 5 estrellas" data-id="element-159" data-mp="2wao367402od7">
              ★★★★★
            </span>{' '}
            4.9/5 · Más de 500 personas ya empezaron
          </p>
        </div>
        <div className="mt-10" role="region" aria-roledescription="carrusel" aria-label="Testimonios" data-id="element-160" data-mp="2n9pxzlhtsat1">
          <div ref={emblaRef} className="cursor-grab overflow-hidden active:cursor-grabbing" data-id="element-161" data-mp="gj8ic1dabx4j">
            <div className="-ml-6 flex touch-pan-y py-1" data-id="element-162" data-mp="ljeffx2bqbu0">
              {TESTIMONIALS.map((t, i) => <div key={t.name} role="group" aria-roledescription="testimonio" aria-label={`${i + 1} de ${TESTIMONIALS.length}`} className="min-w-0 shrink-0 grow-0 basis-full pl-6 md:basis-1/2 lg:basis-1/3" data-id="element-163" data-mp="1nbagronkvpww" data-mpi={i}>
                  <TestimonialCard t={t} data-id="element-164" data-mp="2206yyejcmjb8" />
                </div>)}
            </div>
          </div>
          <div className="mt-6 flex items-center justify-center gap-4" data-id="element-165" data-mp="260h3dmrht1nz">
            <button onClick={prev} aria-label="Testimonio anterior" className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-primary transition-colors duration-150 hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary" data-id="element-166" data-mp="2azaifjmfh0q9">
              <ChevronLeft className="h-4 w-4" aria-hidden="true" data-id="element-167" data-mp="ln8ah95fli68" />
            </button>
            <div className="flex flex-wrap justify-center gap-2" data-id="element-168" data-mp="qkib7ky9tg9d">
              {snaps.map((_, i) => <button key={i} onClick={() => goTo(i)} aria-label={`Ir al grupo de testimonios ${i + 1}`} aria-current={i === selected} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${i === selected ? 'w-6 bg-primary' : 'w-2 bg-primary/30 hover:bg-primary/50'}`} data-id="element-169" data-mp="33wmi6fcau5hp" data-mpi={i} />)}
            </div>
            <button onClick={next} aria-label="Testimonio siguiente" className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-primary transition-colors duration-150 hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary" data-id="element-170" data-mp="3isk7perpwbky">
              <ChevronRight className="h-4 w-4" aria-hidden="true" data-id="element-171" data-mp="1xdwmzzv8l1bx" />
            </button>
          </div>
        </div>
      </div>
    </section>;
}
function HowItWorks() {
  return <section className="w-full bg-blush px-5 py-12 sm:py-20" data-id="element-172" data-mp="23uptjnbh9lzh">
      <div className="mx-auto max-w-5xl" data-id="element-173" data-mp="23twrgzngrkno">
        <div className="mx-auto max-w-2xl text-center" data-id="element-174" data-mp="1pv6stfkzxcx7">
          <h2 className="font-display text-2xl font-bold leading-tight text-foreground sm:text-4xl" data-id="element-175" data-mp="1pundm6mqd2mv">
            ¿Cómo <span className="text-pink" data-id="element-176" data-mp="1uw4c5vhqsd0a">funciona</span>?
          </h2>
          <p className="mt-3 text-sm text-muted sm:text-base" data-id="element-177" data-mp="1g4bx7klv10z3">Simple, inmediato y a tu ritmo.</p>
        </div>
        <div className="mt-12 flex flex-col gap-8 md:flex-row md:gap-6" data-id="element-178" data-mp="1kxepzqitc6e8">
          {STEPS.map((step, i) => {
          const Icon = STEP_ICONS[step.icon];
          return <React.Fragment key={step.number} data-id="element-179" data-mp="1b726m1mqij9o" data-mpi={i}>
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.3
            }} transition={{
              duration: 0.45,
              delay: i * 0.1
            }} className="flex flex-1 flex-col items-center text-center" data-id="element-180" data-mp="x69cvj49bs7m">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full border border-line bg-surface text-primary" data-id="element-181" data-mp="dlraoy7wv35p">
                    <Icon className="h-7 w-7" aria-hidden="true" data-id="element-182" data-mp="sfnj0x42fvsx" />
                  </span>
                  <span className="mt-4 font-display text-2xl font-bold text-primary" data-id="element-183" data-mp="nkq9lp2vjkfi">{step.number}</span>
                  <h3 className="mt-2 text-lg font-bold text-foreground" data-id="element-184" data-mp="11gbtjhu93e12">{step.title}</h3>
                  <p className="mt-3 max-w-xs text-sm leading-relaxed text-muted" data-id="element-185" data-mp="wj1ztpztwg7f">{step.description}</p>
                </motion.div>
                {i < STEPS.length - 1 && <div className="mx-auto h-8 w-px bg-primary/30 md:hidden" aria-hidden="true" data-id="element-186" data-mp="168v8oj8l6h7z" />}
              </React.Fragment>;
        })}
        </div>
      </div>
    </section>;
}
function Guarantee() {
  return <section className="w-full bg-background px-5 py-12 sm:py-20" data-id="element-187" data-mp="1g8vwahdhnjhz">
      <motion.div {...fadeUp} transition={{
      duration: 0.5
    }} className="mx-auto flex max-w-2xl flex-col items-center rounded-2xl border border-line bg-surface p-8 text-center sm:p-12" data-id="element-188" data-mp="39qtfosfz89b5">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary" data-id="element-189" data-mp="3jh5wqaol1kcj">
          <ShieldCheck className="h-9 w-9" aria-hidden="true" data-id="element-190" data-mp="3oeekqrwewilp" />
        </span>
        <h2 className="mt-5 font-display text-2xl font-bold text-foreground sm:text-3xl" data-id="element-191" data-mp="2qs7dfa8q4c5n">
          Garantía de <span className="text-pink" data-id="element-192" data-mp="2vrbpc8n18nzo">7 días</span> sin preguntas
        </h2>
        <p className="mt-2 text-sm font-medium text-primary-strong sm:text-base" data-id="element-193" data-mp="30otxvxx51175">Tu inversión está protegida</p>
        <div className="mt-6 space-y-4 text-sm leading-relaxed text-muted sm:text-base" data-id="element-194" data-mp="34s01exsv8e9x">
          <p data-id="element-195" data-mp="fzw4i2wbhwr0">Accede al método completo, revisa los 6 módulos y los 4 bonos y empieza a estudiar.</p>
          <p data-id="element-196" data-mp="jzy9akxcn6ai">
            Si dentro de los primeros 7 días sientes que no es para ti, te devolvemos el 100% de tu dinero. Sin
            preguntas. Sin trámites complicados.
          </p>
          <p className="font-semibold italic text-foreground" data-id="element-197" data-mp="20gn703s8v9qe">El riesgo es cero. La decisión es tuya.</p>
        </div>
        <div className="mt-8 w-full max-w-md" data-id="element-198" data-mp="1vpew13b9ngb4">
          <CtaButton data-id="element-199" data-mp="1vo3t15pnacl2" />
          <p className="mt-3 text-xs text-muted sm:text-sm" data-id="element-200" data-mp="1qrlq8bdvkobd">Pago único · Acceso inmediato por mail</p>
        </div>
      </motion.div>
    </section>;
}
function Closing() {
  const {
    amount,
    format,
    anchor
  } = usePrice();
  return <section id="cierre" className="w-full scroll-mt-20 bg-ink px-5 py-14 sm:py-24" data-id="element-201" data-mp="1gsef7uum55rf">
      <motion.div initial={{
      opacity: 0,
      y: 24
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      amount: 0.2
    }} transition={{
      duration: 0.5
    }} className="mx-auto flex max-w-2xl flex-col items-center text-center" data-id="element-202" data-mp="1cqsx9v3821ad">
        <h2 className="font-display text-2xl font-bold leading-tight text-surface sm:text-4xl" data-id="element-203" data-mp="326s9jl2506u8">
          Tu primer paso en coreano. Tu método ordenado. <span className="text-pink-light" data-id="element-204" data-mp="3lacatp29affp">Tu plan de 30 días</span> está
          listo para hoy.
        </h2>
        <p className="mt-4 text-lg font-medium text-primary-soft sm:text-xl" data-id="element-205" data-mp="3q3f7i62by504">Aprende Coreano Fácil™</p>
        <ul className="mt-8 grid w-full max-w-md gap-3 text-left" data-id="element-206" data-mp="3ccadbjhqe4x6">
          {CLOSING_ITEMS.map(item => <li key={item} className="flex items-start gap-3" data-id="element-207" data-mp="3h7fy2ezeddjo">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/25 text-primary-soft" data-id="element-208" data-mp="8v1xf56kdf53">
                <Check className="h-3.5 w-3.5" aria-hidden="true" data-id="element-209" data-mp="ct7xxvgt6yhk" />
              </span>
              <span className="text-sm leading-relaxed text-surface/85" data-id="element-210" data-mp="3v0p548zvhmz1">{item}</span>
            </li>)}
        </ul>
        <div className="mt-10 flex flex-col items-center" data-id="element-211" data-mp="2jlryxmbk4h4g">
          <span className="text-lg font-medium text-line line-through sm:text-xl" data-id="element-212" data-mp="2jk75y14w568p">{format(anchor)}</span>
          <span className="font-display text-[48px] font-bold leading-none text-pink-light sm:text-7xl" data-id="element-213" data-mp="2omfxqu612298">
            {format(amount(PRICE_USD))}
          </span>
        </div>
        <motion.a href="#comprar" onClick={e => scrollToSection(e, '#comprar')} animate={{
        y: [0, -2, 0]
      }} whileHover={{
        scale: 1.025,
        backgroundColor: 'rgb(255, 79, 163)',
        boxShadow: '0 14px 32px -8px rgba(255, 79, 163, 0.4)'
      }} whileTap={{
        scale: 0.98
      }} transition={{
        y: {
          duration: 2.4,
          ease: 'easeInOut',
          repeat: Infinity
        },
        type: 'spring',
        stiffness: 400,
        damping: 22
      }} className="mt-9 flex min-h-[58px] w-full max-w-md items-center justify-center gap-2 rounded-cta bg-pink px-6 py-4 text-base font-extrabold text-surface shadow-cta focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-light sm:text-lg" data-id="element-214" data-mp="2c17oi10zporz">
          {CTA_LABEL}
        </motion.a>
      </motion.div>
    </section>;
}
function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  return <section className="w-full bg-background px-5 py-12 sm:py-20" data-id="element-215" data-mp="t3k6e2r3gzu3">
      <div className="mx-auto max-w-2xl" data-id="element-216" data-mp="t4283ewj069e">
        <h2 className="text-center font-display text-2xl font-bold text-foreground sm:text-4xl" data-id="element-217" data-mp="y2wsgla562vm">
          Preguntas <span className="text-pink" data-id="element-218" data-mp="1329ej3t6v5x5">frecuentes</span>
        </h2>
        <div className="mt-8 divide-y divide-line border-y border-line" data-id="element-219" data-mp="12zdffc5lub74">
          {FAQS.map((f, i) => {
          const isOpen = open === i;
          return <div key={f.question} data-id="element-220" data-mp="1c0v4hk0zpn7y" data-mpi={i}>
                <button onClick={() => setOpen(isOpen ? null : i)} aria-expanded={isOpen} className="flex min-h-[48px] w-full items-center justify-between gap-4 py-4 text-left" data-id="element-221" data-mp="1gt4qioyzi34y">
                  <span className="text-sm font-semibold text-foreground sm:text-base" data-id="element-222" data-mp="1qslu90uoe6ya">{f.question}</span>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary" data-id="element-223" data-mp="1vr6swvua4ikm">
                    {isOpen ? <Minus className="h-4 w-4" aria-hidden="true" data-id="element-224" data-mp="3g63dlra8ejk5" /> : <Plus className="h-4 w-4" aria-hidden="true" data-id="element-225" data-mp="3b8bn91p8j5i2" />}
                  </span>
                </button>
                <AnimatePresence initial={false} data-id="element-226" data-mp="36as53qpf28ku">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-227" data-mp="31cgx7liom859">
                      <p className="pb-4 text-sm leading-relaxed text-muted" data-id="element-228" data-mp="3ka19lt49fe8z">{f.answer}</p>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>
    </section>;
}
function Footer() {
  return <footer className="w-full bg-blush px-5 py-10" data-id="element-229" data-mp="27e56y7oys1ha">
      <div className="mx-auto max-w-2xl text-center" data-id="element-230" data-mp="27ey4ci219emr">
        <p className="font-display text-base font-semibold text-foreground" data-id="element-231" data-mp="22fbrwau142f0">Aprende Coreano Fácil™</p>
        <p className="mt-2 text-xs leading-relaxed text-muted" data-id="element-232" data-mp="1yd6zfffbhbty">
          Método visual para aprender coreano desde cero. Descarga inmediata · Pago único · Garantía de 7 días.
        </p>
        <p className="mt-4 text-xs leading-relaxed text-muted" data-id="element-233" data-mp="2q4mw62al8exw">
          © {new Date().getFullYear()} Aprende Coreano Fácil. Todos los derechos reservados. ·{' '}
          <a href="#" className="underline-offset-2 transition-colors duration-150 hover:text-primary-strong hover:underline" data-id="element-234" data-mp="2m3kt1adjviqn">
            Términos y condiciones
          </a>{' '}
          ·{' '}
          <a href="#" className="underline-offset-2 transition-colors duration-150 hover:text-primary-strong hover:underline" data-id="element-235" data-mp="2ccq5m2okid3m">
            Política de privacidad
          </a>
        </p>
      </div>
    </footer>;
}
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < breakpoint : true);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}
function randomPopupIndex(previous: number) {
  if (PURCHASE_POPUPS.length <= 1) return 0;
  let next = previous;
  while (next === previous) next = Math.floor(Math.random() * PURCHASE_POPUPS.length);
  return next;
}

// Popups de compra: misma lógica y frecuencia que antes
function PurchasePopups() {
  const isMobile = useIsMobile();
  const [current, setCurrent] = useState<(typeof PURCHASE_POPUPS)[number] | null>(null);
  const lastIndex = useRef(-1);
  const timers = useRef<number[]>([]);
  const timing = isMobile ? POPUP_TIMING.mobile : POPUP_TIMING.desktop;
  const clearTimers = useCallback(() => {
    timers.current.forEach(id => window.clearTimeout(id));
    timers.current = [];
  }, []);
  useEffect(() => {
    const show = () => {
      const active = document.activeElement;
      if (active instanceof HTMLElement && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) {
        timers.current.push(window.setTimeout(show, 2000));
        return;
      }
      const idx = randomPopupIndex(lastIndex.current);
      lastIndex.current = idx;
      setCurrent(PURCHASE_POPUPS[idx]);
      timers.current.push(window.setTimeout(() => {
        setCurrent(null);
        timers.current.push(window.setTimeout(show, timing.interval));
      }, timing.visible));
    };
    timers.current.push(window.setTimeout(show, timing.initialDelay));
    return clearTimers;
  }, [isMobile]);
  return <div className="pointer-events-none fixed bottom-4 left-3 z-50 sm:bottom-5 sm:left-5" data-id="element-236" data-mp="1catw6uak3eqk">
      <AnimatePresence data-id="element-237" data-mp="1cd6mpvudqxf8">
        {current && <motion.div key={current.name + current.time} initial={{
        x: '-120%',
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        opacity: 0,
        x: 0
      }} transition={{
        x: {
          duration: 0.3,
          ease: 'easeOut'
        },
        opacity: {
          duration: 0.3,
          ease: 'easeOut'
        }
      }} whileHover={{
        boxShadow: '0 14px 34px -10px rgb(var(--color-foreground) / 0.28)'
      }} onClick={() => setCurrent(null)} role="status" aria-live="polite" className="pointer-events-auto w-[220px] max-w-[58vw] cursor-pointer rounded-lg border-l-[3px] border-pink bg-surface px-3.5 py-2.5 shadow-toast sm:w-[280px] sm:max-w-none" data-id="element-238" data-mp="3nz73y5rw87cv">
            <button onClick={e => {
          e.stopPropagation();
          setCurrent(null);
        }} aria-label="Cerrar notificación" className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full text-foreground/40 transition-colors duration-150 hover:text-foreground/70" data-id="element-239" data-mp="309crvqfeemto">
              <X className="h-3.5 w-3.5" aria-hidden="true" data-id="element-240" data-mp="4siryr64ttpd" />
            </button>
            <div className="flex items-center gap-1.5 pr-4" data-id="element-241" data-mp="3vx91z7o1dtnl">
              <CircleCheck className="h-3.5 w-3.5 shrink-0 text-pink" aria-hidden="true" data-id="element-242" data-mp="3vzv95670m75a" />
              <span className="text-[11px] font-bold text-foreground" data-id="element-243" data-mp="3qyfkz59jjkio">Compra verificada</span>
            </div>
            <p className="mt-1 text-[11px] leading-snug text-foreground" data-id="element-244" data-mp="3myx4clor5vln">
              <span className="font-semibold" data-id="element-245" data-mp="3i4syk5r83tm5">{current.name}</span> — {current.location}
            </p>
            <p className="mt-0.5 text-[11px] leading-snug text-foreground" data-id="element-246" data-mp="3d7kc3sz1l3f3">
              adquirió <span className="font-semibold text-pink" data-id="element-247" data-mp="389iwji0trm8d">“Coreano Fácil”</span>
            </p>
            <p className="mt-0.5 text-[10px] text-muted" data-id="element-248" data-mp="337tgtp2nqkdy">{current.time}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}

/* =========================================================
   APP
   ========================================================= */

export function App() {
  return <PriceProvider data-id="element-249" data-mp="1iphk5zobvqh8">
      <div className="w-full bg-background font-sans text-foreground" data-id="element-250" data-mp="1it5bcs6aw0ss">
        <MetaPixel data-id="element-251" data-mp="1nqosq0yaotoy" />
        <GlobalStyles data-id="element-252" data-mp="1no2jlwvra64m" />
        <FloatingEmojis data-id="element-253" data-mp="1sp8i6bc3knm2" />
        <TopBar data-id="element-254" data-mp="1spi7rxt8csr8" />
        <main data-id="element-255" data-mp="1snxe0aeqpduf">
          <Hero data-id="element-256" data-mp="1sogudmoqru6c" />
          <SocialProofBar data-id="element-257" data-mp="1smw0lza94f9j" />
          <Identification data-id="element-258" data-mp="v1zzsp7e7uoa" />
          <ProductIntro data-id="element-259" data-mp="v2j9xjqsxerr" />
          <Modules data-id="element-260" data-mp="v3b2elp3qwdt" />
          <Bonuses data-id="element-261" data-mp="uznb7t74qm29" />
          <Offer data-id="element-262" data-mp="1001i37wzvpyp" />
          <ForWho data-id="element-263" data-mp="101md0yn81ax3" />
          <Testimonials data-id="element-264" data-mp="10147fbeo5kci" />
          <HowItWorks data-id="element-265" data-mp="zyhxx687wurn" />
          <Guarantee data-id="element-266" data-mp="14zxn9ahfhnfu" />
          <Closing data-id="element-267" data-mp="14ze11hkqwcw0" />
          <Faq data-id="element-268" data-mp="14yw1aayvjgjc" />
          <Footer data-id="element-269" data-mp="14ycsbjr7c2hg" />
          <PurchasePopups data-id="element-270" data-mp="14xupu5dy4s13" />
        </main>
      </div>
    </PriceProvider>;
}
export default App;